import { IconName } from 'src/components/ui/Icon/Icon';
import { menuItems } from 'src/components/layouts/Nav/routes';

/**
 * How a nav page is gated by access rights:
 * - omitted        → `hasAccess(page.link)` (exact nav-route match, the default)
 * - `route`        → `hasAccess(link)` against a different route (e.g. a Settings
 *                    tab gated by the `forecasts` feature flag)
 * - `includes`     → `hasAccessIncludes(token)` for Settings sub-tabs whose right
 *                    is matched by substring (mirrors Settings.tsx)
 * - `always`       → always visible (tabs Settings shows unconditionally)
 */
export type NavPageAccess = { kind: 'route'; link: string } | { kind: 'includes'; token: string } | { kind: 'always' };

export type NavPage = { link: string; icon: IconName; labelKey: string; access?: NavPageAccess };

// Flattened, navigable list of menu pages for the command palette. Children
// inherit the parent's icon. A section parent (one that only expands children,
// e.g. "Lager") is pointed at its first child so it never lands on a
// non-existent /section route.
const MENU_PAGES: NavPage[] = menuItems.flatMap((item: any) => {
  const childs = (item.childs ?? []) as { link: string; label: string }[];
  if (childs.length > 0) {
    const parent: NavPage = { link: childs[0].link, icon: item.icon, labelKey: item.label };
    const kids: NavPage[] = childs.map(c => ({ link: c.link, icon: item.icon, labelKey: c.label }));
    return [parent, ...kids];
  }
  return [{ link: item.link, icon: item.icon, labelKey: item.label }];
});

// Settings tabs live in the user overlay, not the main nav, so they are not part
// of menuItems. Each navigates via `settings?tab=<key>` and carries the same
// access gating Settings.tsx applies to that tab.
const SETTINGS_PAGES: NavPage[] = [
  { link: 'settings?tab=my_profile', icon: 'profile', labelKey: 'settings.my_profile', access: { kind: 'always' } },
  { link: 'settings?tab=company', icon: 'company', labelKey: 'settings.company', access: { kind: 'always' } },
  { link: 'settings?tab=cases', icon: 'note', labelKey: 'settings.cases.tab', access: { kind: 'always' } },
  {
    link: 'settings?tab=forecast_planning',
    icon: 'bar-chart',
    labelKey: 'settings.forecast_planning.tab',
    access: { kind: 'route', link: 'forecasts' },
  },
  {
    link: 'settings?tab=agreements',
    icon: 'note',
    labelKey: 'navbar.agreements',
    access: { kind: 'includes', token: 'agreements' },
  },
  { link: 'settings?tab=faq_help', icon: 'help', labelKey: 'settings.faq_help', access: { kind: 'always' } },
  {
    link: 'settings?tab=manage_users',
    icon: 'account',
    labelKey: 'settings.manage_users',
    access: { kind: 'includes', token: 'manage_users' },
  },
  {
    link: 'settings?tab=add_ons_billing',
    icon: 'settings',
    labelKey: 'settings.add_ons_billing',
    access: { kind: 'includes', token: 'add_ons_billing' },
  },
  {
    link: 'settings?tab=mcp_api',
    icon: 'settings',
    labelKey: 'settings.mcp_api.tab',
    access: { kind: 'includes', token: 'mcp_api' },
  },
];

export const NAV_PAGES: NavPage[] = [...MENU_PAGES, ...SETTINGS_PAGES];
